import type { AuthUser } from '#shared/types/auth'
import { useAuthUser } from './useAuthUser'
import {
  useTokenClient,
  type AuthCodeFlowSuccessResponse,
  type AuthCodeFlowErrorResponse
} from 'vue3-google-signin'
import { JwtUtils } from '#shared/utils/jwt'
import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string'
import { useRoute, useRouter } from 'vue-router'
import { useFilters } from './useFilters'

interface TokenRefreshResponse {
  access_token: string
  expires_in: number
  token_type: string
  scope: string
}

// Add custom interface extending AuthCodeFlowSuccessResponse
interface GoogleAuthResponse extends AuthCodeFlowSuccessResponse {
  refresh_token?: string
}

/**
 * Auth composable for handling Google authentication
 * @returns Authentication utilities and state
 */
export const useAuth = () => {
  const router = useRouter()

  // State management using useState
  const authUser = useAuthUser()
  const isInitialized = useState<boolean>('auth-initialized', () => false)
  const isLoading = useState<boolean>('auth-loading', () => false)

  // Use Nuxt's cookie composable with type safety
  const authToken = useCookie<string | null>('auth_token', {
    maxAge: 60 * 60 * 24, // 24 hours
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'strict',
    httpOnly: false,
    path: '/',
    watch: true
  })

  // Add refresh token threshold (5 minutes before expiry)
  const REFRESH_THRESHOLD = 5 * 60 * 1000 // 5 minutes in milliseconds

  /**
   * Decode and validate JWT token
   */
  const decodeJwt = (token: string): AuthUser => {
    try {
      const decodedToken = JwtUtils.decodeToken(token)

      // Instead of throwing error, try to refresh if token is expired or about to expire
      if (JwtUtils.isTokenExpiring(decodedToken, REFRESH_THRESHOLD)) {
        refreshToken()
      }

      return JwtUtils.payloadToUser(decodedToken)
    } catch (error) {
      console.error('[Auth] Failed to decode JWT:', error)
      throw createError({
        statusCode: 401,
        message: 'Invalid authentication token',
        cause: error
      })
    }
  }

  // Token management
  const setToken = (tokenString: string) => {
    authToken.value = tokenString
  }

  const getToken = (): string | null => {
    return authToken.value
  }

  const clearToken = () => {
    authToken.value = null
  }

  /**
   * Handle Google login process
   */
  const loginWithGoogle = async (credential?: string) => {
    console.group('[Auth] Google Login Process')

    if (import.meta.server) {
      console.warn('[Auth] Cannot login from server side')
      return false
    }

    isLoading.value = true

    try {
      if (credential) {
        setToken(credential)
        authUser.value = decodeJwt(credential)
        console.log('[Auth] Login successful via button')
        return true
      } else {
        const { login } = useTokenClient({
          onSuccess: (response: AuthCodeFlowSuccessResponse) => {
            setToken(response.access_token)
            authUser.value = decodeJwt(response.access_token)
            console.log('[Auth] Login successful via token client')
            return true
          },
          onError: (error: AuthCodeFlowErrorResponse) => {
            console.error('[Auth] Login failed:', error)
            clearToken()
            return false
          }
        })

        await login()
        return true
      }
    } catch (err: unknown) {
      console.error('[Auth] Login execution failed:', err)
      clearToken()
      return false
    } finally {
      console.groupEnd()
      isLoading.value = false
    }
  }

  /**
   * Check authentication state
   */
  const checkAuth = () => {
    if (isInitialized.value) return !!getToken()

    const tokenStr = getToken()
    if (tokenStr && !authUser.value) {
      try {
        const decodedToken = JwtUtils.decodeToken(tokenStr)

        if (JwtUtils.isTokenExpiring(decodedToken)) {
          console.warn('[Auth] Token expired, clearing token')
          clearToken()
          return false
        }

        authUser.value = JwtUtils.payloadToUser(decodedToken)
      } catch (error) {
        console.warn('[Auth] Token validation failed, clearing token', error)
        clearToken()
        return false
      }
    }

    isInitialized.value = true
    return !!tokenStr
  }

  /**
   * Handle user sign out
   */
  const signOutUser = async () => {
    console.log('[Auth] Starting sign out process')
    try {
      clearToken()
      authUser.value = null
      isInitialized.value = false
      console.log('[Auth] User signed out successfully')
      await router.push('/signin')
      return true
    } catch (err: unknown) {
      console.error('[Auth] Sign out failed:', err)
      throw createError({
        statusCode: 500,
        message: 'Failed to sign out',
        cause: err
      })
    }
  }

  /**
   * Refresh the authentication token
   */
  const refreshToken = async () => {
    if (import.meta.server) {
      console.warn('[Auth] Cannot refresh token from server side')
      return false
    }

    try {
      const { login } = useTokenClient({
        onSuccess: (response: AuthCodeFlowSuccessResponse) => {
          if (response.access_token) {
            setToken(response.access_token)
            authUser.value = decodeJwt(response.access_token)
            return true
          }
          return false
        },
        onError: (error: AuthCodeFlowErrorResponse) => {
          console.error('[Auth] Token refresh failed:', error)
          return false
        }
      })

      const result = await login()
      return result
    } catch (err: unknown) {
      console.error('[Auth] Token refresh failed:', err)
      return false
    }
  }

  // Initialize auth state on client side
  if (import.meta.client) {
    checkAuth()
  }

  return {
    loginWithGoogle,
    signOutUser,
    checkAuth,
    getToken,
    clearToken,
    refreshToken,
    user: computed(() => authUser.value),
    isInitialized: computed(() => isInitialized.value),
    isLoading: computed(() => isLoading.value)
  }
}

/**
 * Composable to get auth headers for API requests
 */
export const useAuthHeaders = () => {
  // Add null check for SSR
  if (import.meta.server) {
    return {}
  }

  const { getToken } = useAuth()
  const token = getToken()

  return token
    ? {
        Authorization: `Bearer ${token}`
      }
    : {}
}
